import React, { useEffect, useRef } from 'react';

import { InfoCircleOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { Tooltip } from 'antd';
import { colors } from 'theme/colors';

import { useCoreApi } from 'hooks/useApi';

import { formatDateWithTimezone } from 'utils/formatting';

function useChatWithDataTokenUsage(chatIsLoading: boolean): {
  usagePercent: number;
  resetDate: string;
  isLoading: boolean;
} {
  const { data, isLoading, reload } = useCoreApi(schemas.chatWithData.getTokenUsage);
  const wasLoading = useRef(chatIsLoading);

  useEffect(() => {
    if (wasLoading.current && !chatIsLoading) {
      reload();
    }
    wasLoading.current = chatIsLoading;
  }, [chatIsLoading, reload]);

  return {
    usagePercent: data?.usagePercent ?? 0,
    resetDate: data?.resetDate ?? '',
    isLoading,
  };
}

export function TokenUsageBox({ chatIsLoading = false }: { chatIsLoading?: boolean }) {
  const { usagePercent, resetDate, isLoading } = useChatWithDataTokenUsage(chatIsLoading);
  return (
    <div
      className={css`
        padding: 14px 16px;
        border-top: 2px solid ${colors.neutral[300]};
      `}
    >
      <div
        className={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
          color: ${colors.neutral[600]};
          margin-bottom: 6px;
        `}
      >
        <span
          className={css`
            display: flex;
            align-items: center;
            gap: 4px;
          `}
        >
          Token usage · {usagePercent}%
          <Tooltip
            title={`Weekly token usage is shared across all users in your organization. Resets on ${formatDateWithTimezone(
              resetDate,
            )}.`}
          >
            <InfoCircleOutlined
              className={css`
                color: ${colors.neutral[400]};
                font-size: 11px;
                cursor: help;
              `}
            />
          </Tooltip>
        </span>
        {(isLoading || chatIsLoading) && (
          <span
            className={css`
              display: flex;
              gap: 2px;
              align-items: center;

              @keyframes dotPulse {
                0%,
                80%,
                100% {
                  opacity: 0.2;
                }
                40% {
                  opacity: 1;
                }
              }

              span {
                width: 4px;
                height: 4px;
                border-radius: 50%;
                background: ${colors.neutral[600]};
                animation: dotPulse 1.4s infinite ease-in-out;
              }
              span:nth-child(2) {
                animation-delay: 0.2s;
              }
              span:nth-child(3) {
                animation-delay: 0.4s;
              }
            `}
          >
            <span />
            <span />
            <span />
          </span>
        )}
      </div>
      <div
        className={css`
          height: 6px;
          border-radius: 3px;
          background: ${colors.neutral[200]};
          overflow: hidden;
        `}
      >
        <div
          className={css`
            height: 100%;
            width: ${usagePercent}%;
            border-radius: 3px;
            background: ${usagePercent > 90
              ? colors.danger[600]
              : usagePercent > 70
              ? colors.warning[600]
              : colors.primary[600]};
            transition: width 0.3s ease;
          `}
        />
      </div>
    </div>
  );
}
